import { useState, useEffect } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Pagination } from 'rsuite';
import { ActionIconButton } from '@commonClient2/components';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import ReportFilters from '../ReportFilters';
import ReportTable from './ReportTable';
import { loadEmployeePreferences, UPDATE_EMPLOYEE_PREFERENCES_REPORT, downloadReportAsCsv } from '../../../store/employeePreferencesReport';
import { SortOrderType, EmployeeSelectionReportSortColumn } from '../../../Enums';
import './EmployeePreferencesReport.scss';

/** @typedef {'asc'|'desc'} SortType */
const defaultSortType = 'asc';
const pageSizeOptions = [10, 25, 50, 100];
const initialPageSize = 10;

/**
 * @param {number} pageNumber
 * @param {number} totalCount
 * @param {number} pageSize
 * @returns number
 */
const autoFixOutOfBound = (pageNumber, totalCount, pageSize) => Math.min(Math.max(pageNumber, 1), Math.ceil(totalCount / pageSize));

/**
 * @param {{
 * profileId: string,
 * dataviewEmployeeData: ManagerAppData.IEmployee[],
 * filteredDataviewEmployeeData: ManagerAppData.IEmployee[],
 * dataviewFields: ManagerAppData.IDataviewFilter[],
 * isRebid: boolean,
 * showScheduledDateAndTime: boolean,
 * sequentialSchedule: BidProfile.ISequentialEmployeeSchedule | null,
 * employeeFilters: { rank: number; employeeId: number; name: string; }[],
 *  onClose: Function,
 * }} param0
 */
function EmployeePreferencesReport({
    profileId,
    dataviewEmployeeData,
    dataviewFields,
    isRebid,
    showScheduledDateAndTime,
    sequentialSchedule,
    employeeFilters,
    onClose,
}) {
    const { t } = useTranslation('shiftBidding');
    const dispatch = useDispatch();

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { employeePreferencesReport, pendingTasks } = useSelector((state) => state);
    const {
        employeePreferences, filters, pagination, sortParameters,
    } = employeePreferencesReport;
    const { search, selectedEmployees } = filters;
    const { pageSize, pageNumber, totalCount } = pagination;

    const [sortColumnName, setSortColumnName] = useState('rank');
    /** @type {[SortType, Function]} */
    const [sortOrderType, setSortOrderType] = useState(defaultSortType);
    const [filteredEmployeePreferences, setFilteredEmployeePreferences] = useState(employeePreferences);

    /**
     * @param {Partial<EmployeePreferencesReport.IPagination>} pageProps
     */
    const onChangePageProperty = (pageProps) => {
        dispatch({
            type: UPDATE_EMPLOYEE_PREFERENCES_REPORT,
            payload: {
                pagination: {
                    ...pagination,
                    ...pageProps,
                },
            },
        });
    };

    const fetchEmployeePreferencesData = (filters, pagination, sortParameters) => {
        if (selectedEmployees.length && !pendingTasks) {
            dispatch(loadEmployeePreferences(profileId, showScheduledDateAndTime 
                ? sequentialSchedule 
                : null, filters, pagination, sortParameters));
        }
    };

    const handleFilter = () => {
        fetchEmployeePreferencesData(filters, pagination, sortParameters);
    };

    const handlePageNumberChange = (page) => {
        onChangePageProperty({
            pageNumber: page,
        });
        fetchEmployeePreferencesData(filters, { ...pagination, pageNumber: page }, sortParameters);
    };

    /**
     * @param {number} size
     */
    const handleChangePageSize = (size) => {
        localStorage.setItem('employeePreferencesPageSize', `${size}`);
        const paginationToUpdate = {
            pageSize: size,
            pageNumber: autoFixOutOfBound(pageNumber, totalCount, size),
        };
        onChangePageProperty(paginationToUpdate);
        fetchEmployeePreferencesData(filters, { ...pagination, ...paginationToUpdate }, sortParameters);
    };

    /**
     * @param {Partial<EmployeePreferencesReport.IEmployeeReportFilters>} filterProps
     */
    const onChangeFilterProperty = (filterProps) => {
        dispatch({
            type: UPDATE_EMPLOYEE_PREFERENCES_REPORT,
            payload: {
                filters: {
                    ...filters,
                    ...filterProps,
                },
            },
        });
    };

    /**
     * @param {Partial<EmployeePreferencesReport.ISortParameters>} sortProps
     */
    const onChangeSortProperty = (sortProps) => {
        dispatch({
            type: UPDATE_EMPLOYEE_PREFERENCES_REPORT,
            payload: {
                sortParameters: {
                    ...sortParameters,
                    ...sortProps,

                },
            },
        });
    };

    /**
     * @param {string} columnName
     * @param {SortType | undefined} sortType
     */
    const handleSortColumn = (columnName, sortType) => {
        /**
         * @type {EmployeeSelectionReportSortColumn}
         */
        let sortColumn = EmployeeSelectionReportSortColumn.RANK;
        /**
         * @type {SortOrderType}
         */
        let sortOrder = SortOrderType.Ascending;
        switch (columnName) {
            case 'name':
                sortColumn = EmployeeSelectionReportSortColumn.NAME;
                break;
            case 'preference':
                sortColumn = EmployeeSelectionReportSortColumn.PREFERENCE;
                break;
            case 'scheduleTemplateName':
                sortColumn = EmployeeSelectionReportSortColumn.BID_SELECTION;
                break;
            case 'currentScheduleTemplateName':
                sortColumn = EmployeeSelectionReportSortColumn.CURRENT_BID_AWARDING;
                break;
            case 'rank':
            default:
                sortColumn = EmployeeSelectionReportSortColumn.RANK;
                break;
        }
        switch (sortType) {
            case 'desc':
                sortOrder = SortOrderType.Descending;
                break;
            case 'asc':
            default:
                sortOrder = SortOrderType.Ascending;
                break;
        }
        setSortColumnName(columnName);
        setSortOrderType(sortType);
        onChangeSortProperty({ sortColumn, sortOrder });
        fetchEmployeePreferencesData(filters, pagination, { ...sortParameters, sortColumn, sortOrder });
    };

    useEffect(() => {
        setFilteredEmployeePreferences(employeePreferences);
    }, [employeePreferences]);

    useEffect(() => {
        // First time opening, check if opened through report button or dashboard table row
        const employeeIdsFromBid = employeeFilters.map((x) => x.employeeId);
        let employeeIdsToQuery = employeeIdsFromBid;
        if (selectedEmployees.length === 1) {
            employeeIdsToQuery = selectedEmployees;
        }

        dispatch({
            type: UPDATE_EMPLOYEE_PREFERENCES_REPORT,
            payload: {
                filters: {
                    ...filters,
                    selectedEmployees: employeeIdsToQuery,
                },
            },
        });

        dispatch(loadEmployeePreferences(profileId, showScheduledDateAndTime 
            ? sequentialSchedule 
            : null, { ...filters, selectedEmployees: employeeIdsToQuery }, pagination, sortParameters));
    }, []);

    useEffect(() => {
        setFilteredEmployeePreferences(employeePreferences);
    }, [employeePreferences]);
    
    return (
        <>
            <div className="employee-preference-report-header">
                <span className="header-title">{t('common.reports.employeePreferences.title')}</span>
                <ActionIconButton icon={faTimes} onClick={onClose} />
            </div>
            <ReportFilters
                profileId={profileId}
                allEmployeeOptionsInFilter={employeeFilters}
                selectedEmployees={selectedEmployees}
                onChangeSelectedEmployees={(employeeIds) => onChangeFilterProperty({ selectedEmployees: employeeIds })}
                dataviewFields={dataviewFields}
                downloadReportAsCsv={downloadReportAsCsv}
                hasDataviews={!!dataviewEmployeeData.length}
                handleApplyFilter={handleFilter}
                searchString={search}
                onChangeSearchString={(searchString) => onChangeFilterProperty({ search: searchString })}
                onChangeDataviewColumns={(dataviewColumnIds) => onChangeFilterProperty({ dataviewColumnsFilter: dataviewColumnIds })}
            />
            <ReportTable
                data={filteredEmployeePreferences}
                height={600}
                sortColumn={sortColumnName}
                handleSortColumn={handleSortColumn}
                sortType={sortOrderType}
                dataviewFields={dataviewFields}
                dataviewEmployeeData={dataviewEmployeeData}
                isRebid={isRebid}
                showScheduledDateAndTime={showScheduledDateAndTime}
                loading={!!pendingTasks}
            />
            <div className="pagination-wrapper">
                <div className="pagination-desktop">
                    <Pagination
                        prev
                        next
                        first
                        last
                        ellipsis
                        boundaryLinks
                        maxButtons={5}
                        size="xs"
                        layout={['total', '-', 'pager', 'skip', '|', 'limit']}
                        total={totalCount}
                        limitOptions={pageSizeOptions}
                        limit={pageSize}
                        onChangeLimit={(limit) => handleChangePageSize(limit)}
                        activePage={pageNumber}
                        onChangePage={handlePageNumberChange}
                    />
                </div>
                <div className="pagination-mobile">
                    <div className="pagination-mobile-layout">
                        <Pagination
                            prev
                            next
                            first
                            last
                            ellipsis
                            boundaryLinks
                            maxButtons={3}
                            size="xs"
                            layout={['total', '-', 'pager']}
                            total={totalCount}
                            limit={pageSize}
                            activePage={pageNumber}
                            onChangePage={handlePageNumberChange}
                        />
                        <Pagination
                            size="xs"
                            layout={['skip', 'limit']}
                            total={totalCount}
                            limitOptions={pageSizeOptions}
                            limit={pageSize}
                            onChangeLimit={(limit) => handleChangePageSize(limit)}
                            activePage={pageNumber}
                            onChangePage={handlePageNumberChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default EmployeePreferencesReport;
